<template>
  <fragment>
    <ImageCropWrapper
      v-if="component && imageCropComponentID"
      :style="`visibility: ${mode == 'crop_image' ? 'visible' : 'hidden'}`"
    >
      <ImageStopRegion
        @click="$store.dispatch('designIframe/imageCrop/closeImageCrop')"
      />
      <ImageDragArea
        v-show="!loading"
        ref="drag-area"
        @mousedown="dragMouseDown"
        :style="`
          background-image: url('${originalImage}');
          transform: matrix(1, 0, 0, 1, ${translate.translateX}, ${translate.translateY});
          top: ${framePosition.top}px;
          left: ${framePosition.left}px;
          width: ${localDragArea.width}px;
          height: ${localDragArea.height}px;`"
      />
      <ImageFrame
        v-show="!loading"
        id="image-frame"
        :position="framePosition"
        :config="configLocal"
        :is_stretch_height="is_stretch_height"
        :data="component.data"
        :disableCropResize="disableCropResize"
        :translate="translate"
        :style="`
          background-image: url('${originalImage}');
          background-position: ${translate.translateX}px ${
          translate.translateY
        }px;
          top: ${framePosition.top}px;
          left: ${framePosition.left}px;
          width: ${framePosition.width}px;
          height: ${framePosition.height}px;
          background-size: ${is_stretch_height ? 'auto' : ''}  ${
          configLocal.backgroundImageZoom || 100
        }%;`"
      >
        <ImageTool
          class="align-items-center"
          :width="position_tool.width == 0 ? component.style.width : position_tool.width"
          :height="position_tool.height == 0 ? component.style.height : position_tool.height"
          :alignX="component.style.alignX"
          :class="position_tool.class"
        >
          <div variant="unset"><i class="far fa-search-minus"></i></div>
          <div class="flex-grow-1 position-relative" style="height: 26px">
            <Slider
              type="range"
              min="100"
              max="400"
              class="slider w-100"
              v-model.number="backgroundImageZoom"
            />
            <RangeFill
              :style="`width: ${(backgroundImageZoom - 100) / 3}%`"
              style="top: calc(30%)"
            />
          </div>
          <div variant="unset"><i class="far fa-search-plus"></i></div>
          <div class="vl"></div>
          <div
            class="cursor-pointer btn-tool"
            variant="refresh"
            @click="cloneConfigData"
          >
            <i class="far fa-redo-alt"></i>
          </div>

          <div
            class="cursor-pointer btn-tool"
            variant="unset"
            @click="saveConfig"
          >
            <i class="far fa-check"></i>
          </div>
          <div
            class="cursor-pointer btn-tool"
            variant="unset"
            @click="$store.dispatch('designIframe/imageCrop/closeImageCrop')"
          >
            <i class="far fa-times"></i>
          </div>
        </ImageTool>
        <ImageCropResize
          v-if="showResize"
          :component="component"
          :callback="cropResizeCallback"
          :position="framePosition"
          :translate="translate"
          :isDrag="isDrag"
          :isFixRatio="isFixRatio"
          @onDrag="onDrag"
        />
      </ImageFrame>
    </ImageCropWrapper>
  </fragment>
</template>

<script>
import {
  CELL,
  SECTION,
  SLIDE_PAGE,
  SLIDE_ITEM,
  SLIDE_BLOCK,
} from "@model/components/componentMeta.model";
import {
  ImageCropWrapper,
  ImageStopRegion,
  ImageFrame,
  ImageDragArea,
  ImageTool,
} from "./ImageCrop.styled";
import ImageCropResize from "./ImageCropResize.vue";
import { mapGetters } from "vuex";
import {
  Slider,
  RangeFill,
} from "@components_system/Shared/Tools/RangeSlider/RangeSlider.styled";
import {
  checkStretchHeight,
  resizeRatioImage,
} from "@helpers/imageCrop.helper";
import getValuePath from "@helpers/getValuePath.helper";
import setObjectPath from "@helpers/setObjectPath.helper";

export default {
  data() {
    return {
      loading: false,
      init: false,
      translate: {
        translateX: 0,
        translateY: 0,
      },
      backgroundImageZoom: 100,
      backgroundImagePosX: 0,
      backgroundImagePosY: 0,
      image: "",
      imageUrl: "",
      imageDimensions: {
        width: 0,
        height: 0,
      },
      framePosition: {
        width: 0,
        height: 0,
        top: 0,
        left: 0,
      },
      isDrag: false,
      position_tool: {
        value: 0,
        data: {
          class: {
            top: true,
            "d-flex": true,
            "d-none": true,
          },
          height: 0,
          width: 0,
        },
      },
      localDragArea: {
        width: 0,
        height: 0,
        backgroundImageZoom: 0
      },
    };
  },
  computed: {
    ...mapGetters({
      mode: "designIframe/mode",
      imageCropPosition: "designIframe/imageCrop/imageCropPosition",
      imageCropComponentID: "designIframe/imageCrop/imageCropComponentID",
      imageCropMapping: "designIframe/imageCrop/imageCropMapping",
      components: "designIframe/metadata/components",
      siteComponents: "designIframe/metadata/siteComponents",
      scrollPosition: "scrollPosition",
      keypress: "designIframe/windowEvent/keypress",
    }),
    showResize() {
      const is_ignore = [
        SECTION.type,
        SLIDE_PAGE.type,
        CELL.type,
        SLIDE_ITEM.prefixType,
        SLIDE_BLOCK.prefixType,
      ].includes(this.component.type);
      return !is_ignore;
    },
    disableCropResize(){
      const componentsType = [
        'SECTION_WIDGET_INTERACTIVE'
      ].includes(this.component.type);
      return componentsType
    },
    originalImage() {
      return getValuePath(
        this.component,
        `/${this.imageCropMapping.imageUrl}`
      ).replace("{{size}}", "0");
    },
    component() {
      const isSiteComponent = Object.prototype.hasOwnProperty.call(
        this.siteComponents,
        this.imageCropComponentID
      );
      if (isSiteComponent) {
        return this.siteComponents[this.imageCropComponentID];
      } else {
        return this.components[this.imageCropComponentID];
      }
    },

    configLocal() {
      return {
        backgroundImageZoom: this.backgroundImageZoom,
        backgroundImagePosX: this.backgroundImagePosX,
        backgroundImagePosY: this.backgroundImagePosY,
      };
    },
    dragAreaConfig() {
      return resizeRatioImage({
        framePosition: this.framePosition,
        imageZoom: this.backgroundImageZoom,
        imageDimensions: this.imageDimensions,
      });
    },

    is_stretch_height() {
      return checkStretchHeight({
        framePosition: this.framePosition,
        imageDimensions: this.imageDimensions,
      });
    },
    isFixRatio() {
      if (this.component.type == 'SECTION_WIDGET_IMAGE' && this.component.style.shape == 'circle') {
        return true;
      } else if (this.component.type == 'SECTION_WIDGET_YOUTUBE') {
        return true;
      } else {
        return false;
      }
    }
  },
  methods: {
    translatePixelToPct({ translate, scale, frame }) {
      return (-translate / (scale - frame)) * 100;
    },
    translatePctToPixel({ pct, scale, frame }) {
      return (-pct / 100) * (scale - frame);
    },
    cropResizeCallback({ height, width, difftop = 0, diffleft = 0 }) {
      if (height <= 4 || width <= 4) {
        return;
      }
      if (this.translate.translateX + diffleft > 0) {
        return;
      }
      if (this.translate.translateY + difftop > 0) {
        return;
      }
      if (width > this.localDragArea.width) {
        return;
      }
      if (height > this.localDragArea.height) {
        return;
      }
      if (
        this.translate.translateX != 0 &&
        width - this.localDragArea.width > this.translate.translateX + diffleft
      ) {
        return;
      }
      if (
        this.translate.translateY != 0 &&
        height - this.localDragArea.height >
          this.translate.translateY + difftop
      ) {
        return;
      }
      if (
        this.translate.translateX != 0 &&
        width + this.translate.translateX > this.localDragArea.width
      ) {
        return;
      }
      if (
        this.translate.translateY != 0 &&
        this.localDragArea.height <= height + this.translate.translateY
      ) {
        return;
      }

      if (this.framePosition.width != width) {
        this.framePosition.width = parseInt(width);
        this.framePosition.left -= diffleft;
        this.translate.translateX += diffleft;
        this.$nextTick(() => {
          if (!this.is_stretch_height) {
            const newZoom = (this.localDragArea.width / width) * 100;
            this.backgroundImageZoom = newZoom;
          } else {
            const newZoom = (this.localDragArea.height / height) * 100;
            this.backgroundImageZoom = newZoom;
          }
        });
        this.recheckImagePositionEdge();
      }

      if (this.framePosition.height != height) {
        
        this.framePosition.height = parseInt(height);
        this.translate.translateY += difftop;
        this.framePosition.top -= difftop;
        this.$nextTick(() => {
          if (this.is_stretch_height) {
            const newZoom = (this.localDragArea.height / height) * 100;
            this.backgroundImageZoom = newZoom;
          } else {
            const newZoom = (this.localDragArea.width / width) * 100;
            this.backgroundImageZoom = newZoom;
          }
        });
        this.recheckImagePositionEdge();
      }
    },
    saveConfig() {
      const cropPosX = parseInt(
        (-this.translate.translateX * this.imageDimensions.width) /
          this.dragAreaConfig.width
      );

      setObjectPath(
        this.component,
        cropPosX,
        `/${this.imageCropMapping.cropPosX}`
      );
      const cropPosY = parseInt(
        (-this.translate.translateY * this.imageDimensions.height) /
          this.dragAreaConfig.height
      );
      setObjectPath(
        this.component,
        cropPosY,
        `/${this.imageCropMapping.cropPosY}`
      );
      setObjectPath(
        this.component,
        this.backgroundImageZoom,
        `/${this.imageCropMapping.cropZoomPct}`
      );

      const cropWidth = parseInt(
        (this.framePosition.width * this.imageDimensions.width) /
          this.dragAreaConfig.width
      );
      setObjectPath(
        this.component,
        cropWidth,
        `/${this.imageCropMapping.cropWidth}`
      );
      const cropHeight = parseInt(
        (this.framePosition.height * this.imageDimensions.height) /
          this.dragAreaConfig.height
      );
      setObjectPath(
        this.component,
        cropHeight,
        `/${this.imageCropMapping.cropHeight}`
      );
      if (this.imageCropMapping.width) {
        setObjectPath(
          this.component,
          parseInt(this.framePosition.width),
          `/${this.imageCropMapping.width}`
        );
      }
      if (this.imageCropMapping.height) {
        setObjectPath(
          this.component,
          parseInt(this.framePosition.height),
          `/${this.imageCropMapping.height}`
        );
      }

      this.$store.dispatch("designIframe/snapshot/genSnapshot");
      this.$store.dispatch("designIframe/imageCrop/closeImageCrop");
    },

    cloneConfigData() {
      this.framePosition.width = this.imageCropPosition.width;
      this.framePosition.height = this.imageCropPosition.height;
      this.framePosition.top = this.imageCropPosition.top;
      this.framePosition.left = this.imageCropPosition.left;
      this.backgroundImageZoom = getValuePath(
        this.component,
        `/${this.imageCropMapping.cropZoomPct}`,
        0
      );

      const cropPosX = getValuePath(
        this.component,
        `/${this.imageCropMapping.cropPosX}`,
        0
      );
      const cropPosY = getValuePath(
        this.component,
        `/${this.imageCropMapping.cropPosY}`,
        0
      );
      const resizePosX = parseInt(
        -(cropPosX * this.dragAreaConfig.width) / this.imageDimensions.width
      );
      const resizePosY = parseInt(
        -(cropPosY * this.dragAreaConfig.height) / this.imageDimensions.height
      );
      this.translate.translateX = resizePosX;
      this.translate.translateY = resizePosY;

      this.$nextTick(() => {
        this.init = true;
      });
    },
    setImage() {
      if (this.component) {
        this.imageUrl = this.originalImage;
        this.image = "";
        var img = new Image();
        this.loading = true;
        this.$store.dispatch("design/blockloading");
        img.onload = () => {
          this.imageDimensions.height = img.height;
          this.imageDimensions.width = img.width;
          this.cloneConfigData();
          this.setLocalDragArea();
          this.loading = false;
          this.$store.dispatch("design/unblockloading");
        };
        img.src = this.imageUrl;
      }
    },
    dragMouseDown(e) {
      e = e || window.event;
      e.preventDefault();
      // get the mouse cursor position at startup:
      this.pos3 = e.clientX;
      this.pos4 = e.clientY;
      document.onmouseup = this.closeDragElement;
      // call a function whenever the cursor moves:
      document.onmousemove = this.elementDrag;
    },

    elementDrag(e) {
      e = e || window.event;
      e.preventDefault();
      this.pos1 = this.pos3 - e.clientX;
      this.pos2 = this.pos4 - e.clientY;
      this.pos3 = e.clientX;
      this.pos4 = e.clientY;
      this.dragImage({ posX: this.pos1, posY: this.pos2 });
    },
    dragImage({ posX, posY }) {
      this.translate.translateX -= posX;
      this.translate.translateY -= posY;
      this.recheckImagePositionEdge();
    },

    closeDragElement() {
      // stop moving when mouse button is released:
      document.onmouseup = null;
      document.onmousemove = null;
    },
    classPositionTool() {
      const element = document.getElementById("image-frame");
      if (element) {
        const rect = element.getBoundingClientRect();
        const topSpace = rect.top;
        const leftSpace = rect.left;
        const width = rect.width;
        const height = rect.height;
        const rightSpace =
          document.documentElement.clientWidth - (leftSpace + width);
        const bottom =
          document.documentElement.clientHeight - (topSpace + height);
        const toolHeight = -40;
        const toolWidth = -435;

        if (topSpace <= 0 && leftSpace <= 0 && rightSpace <= 0 && bottom <= 0) {
          this.position_tool = {
            class: {
              overscreen: true,
              "d-flex": !this.isDrag,
              "d-none": this.isDrag,
            },
            height: height,
            width: width,
          };
        } else if (topSpace <= 0 && leftSpace <= 0) {
          // Default for ชั่วคราว
          this.position_tool = {
            class: {
              top: true,
              "d-flex": !this.isDrag,
              "d-none": this.isDrag,
            },
            height: height,
            width: width,
          };
        } else {
          this.position_tool = [
            {
              value: topSpace + toolHeight,
              data: {
                class: {
                  top: true,
                  "d-flex": !this.isDrag,
                  "d-none": this.isDrag,
                },
                height: height,
                width: width,
              },
            },
            {
              value: leftSpace + toolWidth,
              data: {
                class: {
                  left: true,
                  "d-flex": !this.isDrag,
                  "d-none": this.isDrag,
                },
                height: height,
                width: width,
              },
            },
            {
              value: rightSpace + toolWidth,
              data: {
                class: {
                  right: true,
                  "d-flex": !this.isDrag,
                  "d-none": this.isDrag,
                },
                height: height,
                width: width,
              },
            },
            {
              value: bottom + toolHeight,
              data: {
                class: {
                  bottom: true,
                  "d-flex": !this.isDrag,
                  "d-none": this.isDrag,
                },
                height: height,
                width: width,
              },
            },
          ].sort((a, b) => b.value - a.value)[0].data;
        }
      }
    },
    recheckImagePositionEdge() {
      this.$nextTick(() => {
        if (
          this.framePosition.height - this.localDragArea.height >
          this.translate.translateY
        ) {
          this.translate.translateY =
            this.framePosition.height - this.localDragArea.height;
        }
        if (
          this.framePosition.width - this.localDragArea.width >
          this.translate.translateX
        ) {
          this.translate.translateX =
            this.framePosition.width - this.localDragArea.width;
        }
        if (this.translate.translateX > 0) {
          this.translate.translateX = 0;
        }
        if (this.translate.translateY > 0) {
          this.translate.translateY = 0;
        }
        if (this.framePosition.height > this.localDragArea.height) {
          this.framePosition.height = parseInt(this.localDragArea.height);
        }
        
        this.translate.translateX = parseInt(this.translate.translateX);
        this.translate.translateY = parseInt(this.translate.translateY);
      });
    },
    onDrag(isDraging) {
      this.isDrag = isDraging;
    },
    setLocalDragArea() {
      this.localDragArea.width = this.dragAreaConfig.width;
      this.localDragArea.height = this.dragAreaConfig.height;
    },
    detectOutOfFrame(e) {
      if(e.clientY <= 0 || e.clientX <= 0 || (e.clientX >= window.innerWidth || e.clientY >= window.innerHeight))
      {
        this.closeDragElement()
      }
    },
  },
  mounted() {
    this.$nextTick(function () {
      this.classPositionTool();
      this.setLocalDragArea();
    });
    document.addEventListener("mouseleave", this.detectOutOfFrame, { passive: true });
  },
  watch: {
    scrollPosition() {
      this.$nextTick(function () {
        this.classPositionTool();
      });
    },
    imageCropComponentID() {
      this.setImage();
      this.$nextTick(function () {
        this.classPositionTool();
      });
    },
    backgroundImageZoom() {
      if (!this.init) return;

      if (!this.isDrag) {
        this.recheckImagePositionEdge();
        this.setLocalDragArea();
      }
    },
    isDrag() {
      this.$nextTick(function () {
        this.classPositionTool();
        this.setLocalDragArea();
      });
    },
    mode() {
      this.$nextTick(function () {
        this.setLocalDragArea();
      });
    },
    keypress() {
      if (this.mode == 'crop_image') {
        if(this.keypress.key == 'Enter') {
          this.saveConfig();
        }
      }
    }
  },
  components: {
    ImageCropWrapper,
    ImageStopRegion,
    ImageFrame,
    ImageDragArea,
    ImageTool,
    Slider,
    RangeFill,
    ImageCropResize,
  },
};
</script>

<style scoped>
.vl {
  border-left: 2px solid #dfdfdf;
  height: 100%;
}
</style>
